import { Dictionary, groupBy, sortBy } from 'lodash';
import { FormQuestion, FormChoiceOption } from 'api/form/types';

export enum DropNodeType {
  Question = 'QUESTION',
  Option = 'OPTION',
}
export interface FormTreeDataNode {
  questionId?: number;
  optionId?: number;
  branchingFrom: number;
  position: number;

  type: DropNodeType;
  level: number;
  title: string;
  key: string;
  selectable?: boolean;
  className: string;
  children: FormTreeDataNode[];
  dragOverGapTop?: boolean;
  dragOverGapBottom?: boolean;
}

const parseTreeStructure = (
  groupedFormQuestions: Dictionary<FormQuestion[]>,
  traverseItemId = -1,
  depthLevel = 1,
): FormTreeDataNode[] => {
  const updatedQuestionsList = [] as FormTreeDataNode[];

  (groupedFormQuestions[traverseItemId] || []).forEach((question) => {
    const updatedQuestion = {
      questionId: question.questionId,
      branchingFrom: question.branchingFrom || -1,
      position: question.position,
      key: `q-${question.questionId}`,
      title: question.questionText,
      type: DropNodeType.Question,
      children: [] as FormTreeDataNode[],
      level: depthLevel,
      className: 'form-question',
    };

    if (question.options) {
      const updatedOptions = [] as FormTreeDataNode[];

      question.options.forEach((option) => {
        const branchedQuestionsList = parseTreeStructure(
          groupedFormQuestions as Dictionary<FormQuestion[]>,
          option.optionId,
          depthLevel + 1,
        );

        // added this check here to avoid option without branches to be not displayed.
        if (branchedQuestionsList.length) {
          updatedOptions.push({
            optionId: option.optionId,
            key: `o-${option.optionId}`,
            title: option.optionLabel,
            type: DropNodeType.Option,
            selectable: false,
            className: 'form-option',
            level: depthLevel + 1,
            children: branchedQuestionsList,
          } as FormTreeDataNode);
        }
      });

      updatedQuestion.children = updatedOptions;
    }

    updatedQuestionsList.push(updatedQuestion as FormTreeDataNode);
  });

  return updatedQuestionsList;
};

export const constructTreeData = (
  flattenedFormQuestions: FormQuestion[],
): FormTreeDataNode[] => {
  const groupedFormQuestion = groupBy(
    sortBy(flattenedFormQuestions, 'position'),
    'branchingFrom',
  );

  return parseTreeStructure(groupedFormQuestion);
};

interface UpdateQuestionsPositionProps {
  sourceQuestionId: number;
  sourceBranchingFrom: number;
  sourcePosition: number;
  destinationBranchingFrom: number;
  destinationPosition: number;
  formQuestions: FormQuestion[];
}
const updateQuestionPositions = ({
  sourceQuestionId,
  formQuestions,
  sourceBranchingFrom,
  sourcePosition,
  destinationBranchingFrom,
  destinationPosition,
}: UpdateQuestionsPositionProps) => {
  return formQuestions.map((question) => {
    // update the position and branching_form of dropped question
    if (question.questionId === sourceQuestionId) {
      return {
        ...question,
        branchingFrom: destinationBranchingFrom,
        position: destinationPosition,
      };
    }

    if (sourceBranchingFrom === destinationBranchingFrom) {
      // Moving items between same branch
      if (
        sourcePosition > destinationPosition &&
        question.position < sourcePosition &&
        question.position >= destinationPosition
      ) {
        return {
          ...question,
          position: question.position + 1,
        };
      }
      if (
        sourcePosition < destinationPosition &&
        question.position <= destinationPosition &&
        question.position > sourcePosition
      ) {
        return {
          ...question,
          position: question.position - 1,
        };
      }
    } else {
      // decrease the position in source branch to account for removed item
      if (
        question.branchingFrom === sourceBranchingFrom &&
        question.position >= sourcePosition
      ) {
        return {
          ...question,
          position: question.position - 1,
        };
      }

      // increase the position in destination branch to accomodate new item
      if (
        question.branchingFrom === destinationBranchingFrom &&
        question.position >= destinationPosition
      ) {
        return {
          ...question,
          position: question.position + 1,
        };
      }
    }

    return question;
  });
};

interface UpdateTreeDataProps {
  flattenedTreeData: FormQuestion[];
  dragNode: FormTreeDataNode;
  dropNode: FormTreeDataNode;
}
interface Result {
  branchingFrom: number;
  position: number;
  updatedFlattenedTreeData: FormQuestion[];
}
export const updateTreeData = ({
  flattenedTreeData,
  dragNode,
  dropNode,
}: UpdateTreeDataProps): Result => {
  let branchingFrom = -1;
  let position = 0;
  const sourceBranchingFrom = dragNode.branchingFrom || -1;

  if (dropNode.type === DropNodeType.Question) {
    branchingFrom = dropNode.branchingFrom || -1;

    // 1. When removing an item from top of the list, we should take the item getting removed into account while calculating position.
    // 2. If the item is dropped at top, position need not be updated.
    if (
      (sourceBranchingFrom === branchingFrom &&
        dropNode.position > dragNode.position) ||
      dropNode.dragOverGapTop
    ) {
      position = dropNode.position;
    } else {
      position = dropNode.position + 1;
    }
  } else if (dropNode.type === DropNodeType.Option) {
    branchingFrom = (dropNode as unknown as FormChoiceOption).optionId;
    position = 1;
  }

  const updatedTreeData = updateQuestionPositions({
    sourceQuestionId: dragNode.questionId!,
    sourceBranchingFrom: dragNode.branchingFrom || -1,
    sourcePosition: dragNode.position,
    destinationBranchingFrom: branchingFrom,
    destinationPosition: position,
    formQuestions: flattenedTreeData,
  });

  return {
    branchingFrom,
    position,
    updatedFlattenedTreeData: updatedTreeData,
  };
};

const getDepth = (object) => {
  return (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    1 + Math.max(0, ...(object.children || []).map((child) => getDepth(child)))
  );
};

export const computeMaxDepthAfterDrop = (
  dragNode: FormTreeDataNode,
  dropNode: FormTreeDataNode,
): number => {
  // Taking into account the each question will have one extra level for options.
  return dropNode.level + Math.floor(getDepth(dragNode) / 2);
};
